import {LoadingButton} from '@mui/lab';
import {Box, Button, TextField, Typography} from '@mui/material';
import {useFormik} from 'formik';
import {useSnackbar} from 'notistack';
import {ReactNode, useState} from 'react';
import * as yup from 'yup';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {
  FREQUENCY_MAX,
  FREQUENCY_MIN,
} from '../../../utils/commtrac-nodes-capabilities';
import {CloseSnackbarAction} from '../../common/CloseSnackbarButton';
import ModalFixed from '../../common/ModalFixed';
import SnackbarMessages from '../../common/SnackbarMessages';

interface FormValues {
  frequency: number | null;
}

interface Props {
  open: boolean;
  ids: number[];
  summary: ReactNode;
  onClose: () => void;
  onSubmitted?: () => void;
}

const CommtracNodeItemsBulkFrequencyModal = ({
  open,
  ids,
  summary,
  onClose,
  onSubmitted,
}: Props) => {
  const {enqueueSnackbar} = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validationSchema = yup.object().shape({
    frequency: yup
      .number()
      .nullable()
      .required('Field is required')
      .integer('Must be an integer')
      .min(FREQUENCY_MIN, `Must be ≥ ${FREQUENCY_MIN}`)
      .max(FREQUENCY_MAX, `Must be ≤ ${FREQUENCY_MAX}`),
  });

  const formik = useFormik<FormValues>({
    initialValues: {frequency: null},
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        await API.patch(
          `${apiBaseUrl}/commtrac-node/miner/report_frequency`,
          {
            ids,
            frequency: values.frequency,
          }
        );
        enqueueSnackbar(
          'Broadcast frequency update has been successfully sent',
          {variant: 'success', action: CloseSnackbarAction}
        );
        onSubmitted?.();
        onClose();
        formik.resetForm();
      } catch (error: any) {
        const messages = getMessagesFromApiError(error, 'STRA_bulkupd_02');
        enqueueSnackbar(<SnackbarMessages messages={messages} />, {
          variant: 'error',
          action: CloseSnackbarAction,
        });
      }
      setIsSubmitting(false);
    },
  });

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    formik.resetForm();
    onClose();
  };

  return (
    <ModalFixed open={open} onClose={handleClose}>
      <Box
        component="form"
        display="flex"
        flexDirection="column"
        gap={3}
        p={3}
        onSubmit={formik.handleSubmit}
      >
        <Typography variant="h6">Set Reporting Frequency</Typography>
        <Typography variant="body2" color="text.secondary">
          {summary}
        </Typography>
        <TextField
          value={formik.values.frequency ?? ''}
          label="Reporting Frequency (s)"
          size="small"
          name="frequency"
          type="number"
          fullWidth
          error={!!formik.touched.frequency && !!formik.errors.frequency}
          helperText={formik.touched.frequency && formik.errors.frequency}
          inputProps={{min: FREQUENCY_MIN, max: FREQUENCY_MAX, step: 1}}
          onChange={(e) =>
            formik.setFieldValue(
              e.target.name,
              e.target.value ? Math.abs(+e.target.value) : null
            )
          }
        />
        <Box display="flex" justifyContent="end" gap={1}>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            type="submit"
            loading={isSubmitting}
          >
            Apply
          </LoadingButton>
        </Box>
      </Box>
    </ModalFixed>
  );
};

export default CommtracNodeItemsBulkFrequencyModal;
